import React, { useState } from 'react';
import { numbersOnly } from '../utils/inputValidation';
// ...existing code...
import styles from '../css-isolated/hr_psychiatric_history.module.css';

function PsychiatricHistory({ values, onChange, onSubmit, isDarkMode = true, sex, openDatePicker, activeSection: externalActiveSection, setActiveSection: externalSetActiveSection }) {
  // Active section state (1 = Psychiatric History, 2 = ARV Treatment + OB/GYNE & Vaccines)
  // Use external state if provided (from HRWizard), otherwise use internal state
  const [internalActiveSection] = useState(1);
  const activeSection = externalActiveSection !== undefined ? externalActiveSection : internalActiveSection;

  // Helper function to format date for display (MM/DD/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'Select Date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Select Date';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const [formData, setFormData] = useState(values || {
    // Past Psychiatric History
    consultationWithPsychiatrist: '',
    consultationWhen: '',
    consultationWhere: '',
    confinementInPsychiatricFacility: '',
    confinementWhen: '',
    diagnosis: '',
    medicationTaken: '',
    
    // ARV Treatment
    arvTreatment: '',
    arvTreatmentFrom: '',
    arvTreatmentPlace: '',
    
    // OB/GYNE History
    obGyneApplicable: '',
    lmp: '',
    gravida: '',
    parity: '',
    
    // Vaccines
    vaccines: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...formData, [name]: value };

    // When user selects 'No' for confinement, clear related fields
    if (name === 'confinementInPsychiatricFacility' && value === 'No') {
      updated = {
        ...updated,
        confinementWhen: '',
        diagnosis: '',
        medicationTaken: ''
      };
    }

    // When user selects 'No' for consultation with psychiatrist, clear related fields
    if (name === 'consultationWithPsychiatrist' && value === 'No') {
      updated = {
        ...updated,
        consultationWhen: '',
        consultationWhere: ''
      };
    }

    // When user selects 'No' for ARV treatment, clear related fields
    if (name === 'arvTreatment' && value === 'No') {
      updated = {
        ...updated,
        arvTreatmentFrom: '',
        arvTreatmentPlace: ''
      };
    }

    // When user selects 'No' for OB/GYNE applicability, clear related fields
    if (name === 'obGyneApplicable' && value === 'No') {
      updated = {
        ...updated,
        lmp: '',
        gravida: '',
        parity: '',
        vaccines: ''
      };
    }
    setFormData(updated);
    if (onChange) onChange(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(formData);
  };

  // Check if OB-GYN section should be disabled
  const isObGyneDisabled = !sex || sex !== 'F';

  return (
    <div className={`${styles['hr-wizard-container']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
        <form onSubmit={handleSubmit} autoComplete="off" className={styles.contentWrapper}>
          {/* Section 1: Past Psychiatric History */}
          {activeSection === 1 && (
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Past Psychiatric History</h2>
            </div>
              <div className={styles.formGrid}>
                {/* Compact Three Column Layout */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Psychiatrist Consultation</label>
                    <div className={styles.radioGroup}>
                      {['Yes', 'No'].map(option => (
                        <label key={option} className={styles.radioOption}>
                          <input
                            type="radio"
                            name="consultationWithPsychiatrist"
                            value={option}
                            checked={formData.consultationWithPsychiatrist === option}
                            onChange={handleInputChange}
                            className={styles.radioInput}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>When (MM/DD/YY)</label>
                    <div 
                      className={styles.datePickerField}
                      onClick={() => {
                        if (formData.consultationWithPsychiatrist === 'Yes' && openDatePicker) {
                          openDatePicker(formData.consultationWhen || '', (selectedDate) => {
                            const updated = { ...formData, consultationWhen: selectedDate };
                            setFormData(updated);
                            if (onChange) onChange(updated);
                          });
                        }
                      }}
                      style={{ 
                        opacity: formData.consultationWithPsychiatrist !== 'Yes' ? 0.5 : 1,
                        cursor: formData.consultationWithPsychiatrist !== 'Yes' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <span className={styles.datePickerIcon}>📅</span>
                      <span className={formData.consultationWhen ? styles.datePickerValue : styles.datePickerPlaceholder}>
                        {formatDateForDisplay(formData.consultationWhen)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Where</label>
                    <input
                      type="text"
                      name="consultationWhere"
                      value={formData.consultationWhere}
                      onChange={handleInputChange}
                      className={styles.formInput}
                      placeholder="Facility/location"
                      disabled={formData.consultationWithPsychiatrist !== 'Yes'}
                      autoComplete='off'
                    />
                  </div>
                </div>

                {/* Second Row */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Psychiatric Facility Confinement</label>
                    <div className={styles.radioGroup}>
                      {['Yes', 'No'].map(option => (
                        <label key={option} className={styles.radioOption}>
                          <input
                            type="radio"
                            name="confinementInPsychiatricFacility"
                            value={option}
                            checked={formData.confinementInPsychiatricFacility === option}
                            onChange={handleInputChange}
                            className={styles.radioInput}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>When (MM/DD/YY)</label>
                    <div 
                      className={styles.datePickerField}
                      onClick={() => {
                        if (formData.confinementInPsychiatricFacility === 'Yes' && openDatePicker) {
                          openDatePicker(formData.confinementWhen || '', (selectedDate) => {
                            const updated = { ...formData, confinementWhen: selectedDate };
                            setFormData(updated);
                            if (onChange) onChange(updated);
                          });
                        }
                      }}
                      style={{ 
                        opacity: formData.confinementInPsychiatricFacility !== 'Yes' ? 0.5 : 1,
                        cursor: formData.confinementInPsychiatricFacility !== 'Yes' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <span className={styles.datePickerIcon}>📅</span>
                      <span className={formData.confinementWhen ? styles.datePickerValue : styles.datePickerPlaceholder}>
                        {formatDateForDisplay(formData.confinementWhen)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Diagnosis</label>
                    <textarea
                      name="diagnosis"
                      value={formData.diagnosis}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Diagnosis details..."
                      disabled={formData.confinementInPsychiatricFacility !== 'Yes'}
                    />
                  </div>
                </div>

                {/* Third Row */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Medication Taken</label>
                    <textarea
                      name="medicationTaken"
                      value={formData.medicationTaken}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Medications..."
                      disabled={formData.confinementInPsychiatricFacility !== 'Yes'}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    {/* Empty for layout balance */}
                  </div>
                  <div className={styles.formGroup}>
                    {/* Empty for layout balance */}
                  </div>
                </div>
              </div>
            </div>
          )}

            {/* Section 2: ARV Treatment */}
            {activeSection === 2 && (
            <>
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h2 className={styles.sectionTitle}>ARV Treatment</h2>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>ARV Treatment</label>
                    <div className={styles.radioGroup}>
                      {['Yes', 'No'].map(option => (
                        <label key={option} className={styles.radioOption}>
                          <input
                            type="radio"
                            name="arvTreatment"
                            value={option}
                            checked={formData.arvTreatment === option}
                            onChange={handleInputChange}
                            className={styles.radioInput}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>From (MM/DD/YY)</label>
                    <div 
                      className={styles.datePickerField}
                      onClick={() => {
                        if (formData.arvTreatment === 'Yes' && openDatePicker) {
                          openDatePicker(formData.arvTreatmentFrom || '', (selectedDate) => {
                            const updated = { ...formData, arvTreatmentFrom: selectedDate };
                            setFormData(updated);
                            if (onChange) onChange(updated);
                          });
                        }
                      }}
                      style={{ 
                        opacity: formData.arvTreatment !== 'Yes' ? 0.5 : 1,
                        cursor: formData.arvTreatment !== 'Yes' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <span className={styles.datePickerIcon}>📅</span>
                      <span className={formData.arvTreatmentFrom ? styles.datePickerValue : styles.datePickerPlaceholder}>
                        {formatDateForDisplay(formData.arvTreatmentFrom)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Place</label>
                    <input
                      type="text"
                      name="arvTreatmentPlace"
                      value={formData.arvTreatmentPlace}
                      onChange={handleInputChange}
                      className={styles.formInput}
                      placeholder="Treatment facility"
                      disabled={formData.arvTreatment !== 'Yes'}
                      autoComplete='off'
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: OB/GYNE History & Vaccines - Now on Page 2 */}
            <div className={`${styles.formSection} ${isObGyneDisabled ? styles.disabledSection : ''}`}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h2 className={styles.sectionTitle}>OB/GYNE History & Vaccines</h2>
              </div>
              <div className={styles.formGrid}>
                {/* Compact Layout */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>OB/GYNE Applicable</label>
                    <div className={styles.radioGroup}>
                      {['Yes', 'No'].map(option => (
                        <label key={option} className={styles.radioOption}>
                          <input
                            type="radio"
                            name="obGyneApplicable"
                            value={option}
                            checked={formData.obGyneApplicable === option}
                            onChange={handleInputChange}
                            className={styles.radioInput}
                            disabled={isObGyneDisabled}
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>LMP (MM/DD/YY)</label>
                    <div 
                      className={styles.datePickerField}
                      onClick={() => {
                        if (!isObGyneDisabled && formData.obGyneApplicable === 'Yes' && openDatePicker) {
                          openDatePicker(formData.lmp || '', (selectedDate) => {
                            const updated = { ...formData, lmp: selectedDate };
                            setFormData(updated);
                            if (onChange) onChange(updated);
                          });
                        }
                      }}
                      style={{ 
                        opacity: (isObGyneDisabled || formData.obGyneApplicable !== 'Yes') ? 0.5 : 1,
                        cursor: (isObGyneDisabled || formData.obGyneApplicable !== 'Yes') ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <span className={styles.datePickerIcon}>📅</span>
                      <span className={formData.lmp ? styles.datePickerValue : styles.datePickerPlaceholder}>
                        {formatDateForDisplay(formData.lmp)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Vaccines</label>
                    <textarea
                      name="vaccines"
                      value={formData.vaccines}
                      onChange={handleInputChange}
                      className={styles.formTextarea}
                      placeholder="Known vaccines..."
                      disabled={isObGyneDisabled || formData.obGyneApplicable !== 'Yes'}
                    />
                  </div>
                </div>

                {/* Second Row */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>G (Gravida)</label>
                    <input
                      type="text"
                      name="gravida"
                      value={formData.gravida}
                      onChange={handleInputChange}
                      onKeyDown={numbersOnly}
                      className={styles.formInput}
                      placeholder="Gravida"
                      disabled={isObGyneDisabled || formData.obGyneApplicable !== 'Yes'}
                      autoComplete='off'
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>P (Parity)</label>
                    <input
                      type="text"
                      name="parity"
                      value={formData.parity}
                      onChange={handleInputChange}
                      onKeyDown={numbersOnly}
                      className={styles.formInput}
                      placeholder="Parity"
                      disabled={isObGyneDisabled || formData.obGyneApplicable !== 'Yes'}
                      autoComplete='off'
                    />
                  </div>
                  <div className={styles.formGroup}>
                    {/* Empty for layout balance */}
                  </div>
                </div>
              </div>
            </div>
            </>
            )}

        </form>
    </div>
  );
}

export default PsychiatricHistory;